import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { toast } from 'react-toastify';
import ReportBlockMenu from '../components/social/ReportBlockMenu';

// ── Icons (inline SVG) ──────────────────────────────────────────────────────

const Icon = {
  Pencil: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  Back: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
  Close: (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Send: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Bubble: (props) => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  Check: (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Search: (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Users: (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  User: (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getConvName = (conv, myId) => {
  if (conv.type === 'group') return conv.name || conv.participants.map(p => p.user.username).join(', ');
  const other = conv.participants.find(p => p.user.id !== myId);
  return other?.user?.username || 'Unknown';
};

const getConvAvatar = (conv, myId) => {
  if (conv.type === 'dm') {
    const other = conv.participants.find(p => p.user.id !== myId);
    return other?.user ? [other.user] : [];
  }
  return conv.participants.filter(p => p.user.id !== myId).slice(0, 2).map(p => p.user);
};

const Avatar = ({ user, size = 38 }) => {
  if (user?.profileImageUrl) {
    return <img src={user.profileImageUrl} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <div className="msg-avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {user?.username?.[0]?.toUpperCase() || '?'}
    </div>
  );
};

const ConvAvatars = ({ users, size = 42 }) => {
  if (users.length === 0) return <Avatar user={null} size={size} />;
  if (users.length === 1) return <Avatar user={users[0]} size={size} />;
  const sub = Math.round(size * 0.7);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 0, left: 0 }}><Avatar user={users[0]} size={sub} /></div>
      <div style={{ position: 'absolute', bottom: 0, right: 0 }}><Avatar user={users[1]} size={sub} /></div>
    </div>
  );
};

// ── New Message Modal ────────────────────────────────────────────────────────

const NewMessageModal = ({ onClose, onOpenConv }) => {
  const [mode, setMode] = useState('dm'); // 'dm' | 'group'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [following, setFollowing] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
    api.get('/messages/following').then(r => setFollowing(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get('/messages/search-users', { params: { q: query } })
        .then(r => setResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const toggleSelect = (user) => {
    setSelected(prev => prev.find(u => u.id === user.id)
      ? prev.filter(u => u.id !== user.id)
      : [...prev, user]
    );
  };

  const displayList = query.trim() ? results : following;

  const handleStart = async () => {
    if (!selected.length) return;
    setCreating(true);
    try {
      if (mode === 'dm' || selected.length === 1) {
        const { data } = await api.post('/messages/conversations/dm', { userId: selected[0].id });
        onOpenConv(data);
      } else {
        const { data } = await api.post('/messages/conversations/group', {
          name: groupName.trim() || null,
          userIds: selected.map(u => u.id),
        });
        onOpenConv(data);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal msg-new-modal" onClick={e => e.stopPropagation()}>
        <div className="msg-new-header">
          <h3 className="msg-new-title">New Message</h3>
          <button onClick={onClose} className="msg-new-close" aria-label="Close">
            <Icon.Close />
          </button>
        </div>

        <div className="msg-mode-tabs" role="tablist">
          <button
            className={`msg-mode-tab${mode === 'dm' ? ' active' : ''}`}
            onClick={() => setMode('dm')}
            role="tab"
          >
            <Icon.User /> Direct
          </button>
          <button
            className={`msg-mode-tab${mode === 'group' ? ' active' : ''}`}
            onClick={() => setMode('group')}
            role="tab"
          >
            <Icon.Users /> Group
          </button>
        </div>

        <div className="msg-new-body">
          {mode === 'group' && selected.length >= 2 && (
            <input
              className="msg-glass-input"
              placeholder="Group name (optional)"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              style={{ marginBottom: 14 }}
            />
          )}

          {selected.length > 0 && (
            <div className="msg-chip-row">
              {selected.map(u => (
                <span key={u.id} className="msg-chip" onClick={() => toggleSelect(u)}>
                  {u.username}
                  <Icon.Close />
                </span>
              ))}
            </div>
          )}

          <div className="msg-search-wrap">
            <span className="msg-search-icon"><Icon.Search /></span>
            <input
              ref={searchRef}
              className="msg-glass-input msg-search-input"
              placeholder="Search people"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>

          {!query.trim() && following.length > 0 && (
            <p className="msg-section-label">People you follow</p>
          )}

          <div className="msg-user-list">
            {displayList.map(u => {
              const isSelected = !!selected.find(s => s.id === u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  className={`msg-user-row${isSelected ? ' selected' : ''}`}
                  onClick={() => mode === 'dm' ? setSelected([u]) : toggleSelect(u)}
                >
                  <Avatar user={u} size={36} />
                  <span className="msg-user-name">{u.username}</span>
                  <span className={`msg-user-check${isSelected ? ' on' : ''}`}>
                    {isSelected && <Icon.Check />}
                  </span>
                </button>
              );
            })}
            {displayList.length === 0 && query.trim() && (
              <p className="msg-empty-row">No users found</p>
            )}
            {displayList.length === 0 && !query.trim() && (
              <p className="msg-empty-row">Search to find people to message</p>
            )}
          </div>
        </div>

        <div className="msg-new-footer">
          <button className="msg-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="msg-btn-primary"
            disabled={!selected.length || creating}
            onClick={handleStart}
          >
            {creating ? 'Starting' : (mode === 'group' && selected.length >= 2) ? 'Create group' : 'Open chat'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const Messages = () => {
  const { user } = useSelector(s => s.auth);
  const myId = user?.id;

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const activeConvRef = useRef(null);
  activeConvRef.current = activeConv;
  const lastMsgCountRef = useRef(0);
  const deepLinkedRef = useRef(false);

  // ── Fetch conversations ──
  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get('/messages/conversations');
      setConversations(data);
    } catch {}
  }, []);

  // ── Fetch messages for active conversation ──
  const fetchMessages = useCallback(async (convId, silent = false) => {
    if (!silent) setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/messages/conversations/${convId}/messages`);
      setMessages(data);
    } catch {}
    if (!silent) setLoadingMsgs(false);
  }, []);

  useEffect(() => { fetchConversations(); }, []);

  // ── Open a specific thread from ?c=<conversationId> ──
  // Both entry points land here: the notification bell (NotificationBell resolves
  // relatedType 'conversation' to this URL) and a web-push click (public/sw.js:38).
  // The conversation list is the source of truth, so wait for it to arrive; if the
  // thread isn't in the first response the 10s poll gives us another chance.
  useEffect(() => {
    const target = searchParams.get('c');
    if (!target || deepLinkedRef.current || !conversations.length) return;
    const conv = conversations.find((c) => String(c.id) === String(target));
    if (!conv) return;
    deepLinkedRef.current = true;
    setActiveConv(conv);
    // Drop the param so refreshing or going back doesn't force the thread open again.
    const next = new URLSearchParams(searchParams);
    next.delete('c');
    setSearchParams(next, { replace: true });
  }, [conversations, searchParams, setSearchParams]);

  useEffect(() => {
    const id = setInterval(fetchConversations, 10000);
    return () => clearInterval(id);
  }, [fetchConversations]);

  useEffect(() => {
    if (!activeConv) { setMessages([]); return; }
    fetchMessages(activeConv.id);
    api.put(`/messages/conversations/${activeConv.id}/read`).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [activeConv?.id]);

  useEffect(() => {
    if (!activeConv) return;
    const id = setInterval(() => {
      if (activeConvRef.current) fetchMessages(activeConvRef.current.id, true);
    }, 3000);
    return () => clearInterval(id);
  }, [activeConv?.id]);

  // Scroll the messages container itself (not the page) when new messages
  // arrive. Skip if the user has scrolled up to read older messages — we
  // shouldn't yank them back down on every 3s poll.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const newCount = messages.length;
    const prevCount = lastMsgCountRef.current;
    lastMsgCountRef.current = newCount;

    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const wasNearBottom = distFromBottom < 80;
    const isFirstLoad = prevCount === 0 && newCount > 0;

    if (isFirstLoad || (newCount > prevCount && wasNearBottom)) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Reset scroll-tracking when switching conversations.
  useEffect(() => {
    lastMsgCountRef.current = 0;
  }, [activeConv?.id]);

  const handleSend = async () => {
    if (!input.trim() || !activeConv || sending) return;
    setSending(true);
    const content = input.trim();
    setInput('');
    try {
      const { data } = await api.post(`/messages/conversations/${activeConv.id}/messages`, { content });
      setMessages(prev => [...prev, data]);
      fetchConversations();
    } catch {
      setInput(content);
      toast.error('Failed to send message');
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleOpenConv = (conv) => {
    setActiveConv(conv);
    setConversations(prev => {
      const exists = prev.find(c => c.id === conv.id);
      if (exists) return prev;
      return [{ ...conv, lastMessage: null, unreadCount: 0 }, ...prev];
    });
    fetchConversations();
  };

  return (
    <div className="messages-page">

      {/* Two-pane layout */}
      <div className={`msg-page${activeConv ? ' msg-mobile-thread' : ''}`}>

        {/* ── Left panel ── */}
        <aside className="msg-sidebar">
          <div className="msg-sidebar-actions">
            <button
              type="button"
              className="msg-sidebar-new-btn"
              onClick={() => setShowNewModal(true)}
              aria-label="New message"
              title="New message"
            >
              <Icon.Pencil />
            </button>
          </div>
          <div className="msg-conv-list">
            {conversations.length === 0 && (
              <div className="msg-sidebar-empty">
                <Icon.Bubble />
                <p>No conversations yet</p>
                <button className="msg-btn-primary msg-btn-primary-sm" onClick={() => setShowNewModal(true)}>
                  <Icon.Pencil />
                  Start one
                </button>
              </div>
            )}
            {conversations.map(conv => {
              const avatarUsers = getConvAvatar(conv, myId);
              const name = getConvName(conv, myId);
              const isActive = activeConv?.id === conv.id;
              const isUnread = conv.unreadCount > 0;
              const preview = conv.lastMessage
                ? (conv.lastMessage.sender?.id === myId ? 'You: ' : '') + conv.lastMessage.content
                : 'No messages yet';
              return (
                <button
                  key={conv.id}
                  type="button"
                  className={`msg-conv-row${isActive ? ' active' : ''}${isUnread ? ' unread' : ''}`}
                  onClick={() => setActiveConv(conv)}
                >
                  <ConvAvatars users={avatarUsers} size={44} />
                  <div className="msg-conv-info">
                    <div className="msg-conv-row-top">
                      <span className="msg-conv-name">{name}</span>
                      <span className="msg-conv-time">{fmt(conv.lastMessage?.createdAt || conv.updatedAt)}</span>
                    </div>
                    <div className="msg-conv-row-bottom">
                      <span className="msg-conv-preview">{preview}</span>
                      {isUnread && (
                        <span className="msg-unread-badge">{conv.unreadCount > 9 ? '9+' : conv.unreadCount}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ── Right panel ── */}
        <section className="msg-thread">
          {!activeConv ? (
            <div className="msg-empty-state">
              <div className="msg-empty-icon"><Icon.Bubble /></div>
              <p className="msg-empty-title">Your messages</p>
              <p className="msg-empty-sub">Pick a conversation from the list, or start a new one.</p>
              <button className="msg-btn-primary" style={{ marginTop: 18 }} onClick={() => setShowNewModal(true)}>
                <Icon.Pencil />
                New message
              </button>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="msg-thread-header" style={{ display: 'flex', alignItems: 'center' }}>
                <button className="msg-back-btn" onClick={() => setActiveConv(null)} aria-label="Back to list">
                  <Icon.Back />
                </button>
                <ConvAvatars users={getConvAvatar(activeConv, myId)} size={40} />
                <div className="msg-thread-title-block" style={{ flex: 1, minWidth: 0 }}>
                  {activeConv.type === 'dm' ? (
                    (() => {
                      const other = activeConv.participants?.find(p => p.user.id !== myId)?.user;
                      return other
                        ? <Link to={`/profile/${other.username}`} className="msg-thread-title">{other.username}</Link>
                        : <span className="msg-thread-title">Unknown</span>;
                    })()
                  ) : (
                    <span className="msg-thread-title">{getConvName(activeConv, myId)}</span>
                  )}
                  {activeConv.type === 'group' && (
                    <div className="msg-thread-sub">
                      {activeConv.participants?.map(p => p.user.username).join(', ')}
                    </div>
                  )}
                  {activeConv.type === 'dm' && (
                    <div className="msg-thread-sub">Direct message</div>
                  )}
                </div>
                {activeConv.type === 'dm' && (() => {
                  const other = activeConv.participants?.find(p => p.user.id !== myId)?.user;
                  if (!other) return null;
                  return (
                    <ReportBlockMenu
                      targetType="user"
                      targetId={other.id}
                      targetUserId={other.id}
                      targetLabel="profile"
                      onBlocked={() => {
                        setConversations(prev => prev.filter(c => c.id !== activeConv.id));
                        setActiveConv(null);
                      }}
                    />
                  );
                })()}
              </div>

              {/* Messages */}
              <div className="msg-messages" ref={messagesContainerRef}>
                {loadingMsgs && messages.length === 0 && (
                  <div className="msg-thread-loading">Loading messages</div>
                )}
                {!loadingMsgs && messages.length === 0 && (
                  <div className="msg-thread-loading">No messages yet — say hello.</div>
                )}
                {messages.map((msg, i) => {
                  const isMe = msg.sender?.id === myId || msg.senderId === myId;
                  const senderId = msg.sender?.id || msg.senderId;
                  const prevMsg = messages[i - 1];
                  const showSender = !isMe && activeConv.type === 'group' &&
                    (!prevMsg || prevMsg.sender?.id !== msg.sender?.id);
                  const groupedWithPrev = prevMsg
                    && (prevMsg.sender?.id === msg.sender?.id || prevMsg.senderId === msg.senderId)
                    && (new Date(msg.createdAt) - new Date(prevMsg.createdAt)) < 60000;
                  const bubbleUser = isMe ? user : msg.sender;
                  return (
                    <div
                      key={msg.id}
                      className={`msg-bubble-wrap${isMe ? ' me' : ''}${groupedWithPrev ? ' grouped' : ''}`}
                    >
                      <div className="msg-bubble-avatar">
                        {!groupedWithPrev ? <Avatar user={bubbleUser} size={28} /> : <div style={{ width: 28 }} />}
                      </div>
                      <div className="msg-bubble-col" style={{ position: 'relative' }}>
                        {showSender && (
                          <span className="msg-bubble-sender">{msg.sender?.username}</span>
                        )}
                        <div className={`msg-bubble${isMe ? ' me' : ''}`} style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6 }}>
                          <span>{msg.content}</span>
                          {!isMe && senderId && (
                            <ReportBlockMenu
                              targetType="message"
                              targetId={msg.id}
                              targetUserId={senderId}
                              targetLabel="message"
                              onReported={() => setMessages(ms => ms.filter(m => m.id !== msg.id))}
                              onBlocked={() => {
                                if (activeConv.type === 'dm') {
                                  setConversations(prev => prev.filter(c => c.id !== activeConv.id));
                                  setActiveConv(null);
                                } else {
                                  setMessages(ms => ms.filter(m => (m.sender?.id || m.senderId) !== senderId));
                                }
                              }}
                              triggerSize="small"
                              triggerStyle={{ color: isMe ? '#0a0a0a' : '#9aa3b2' }}
                            />
                          )}
                        </div>
                        <span className={`msg-bubble-time${isMe ? ' me' : ''}`}>{fmt(msg.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Input */}
              <div className="msg-input-bar">
                <textarea
                  ref={inputRef}
                  className="msg-glass-input msg-textarea"
                  placeholder="Type a message"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
                <button
                  className="msg-send-btn"
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  aria-label="Send message"
                >
                  <Icon.Send />
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      {showNewModal && (
        <NewMessageModal
          onClose={() => setShowNewModal(false)}
          onOpenConv={handleOpenConv}
        />
      )}
    </div>
  );
};

export default Messages;
